import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { MedplumClient } from "@medplum/core";
import type { Observation, Reference, Resource } from "@medplum/fhirtypes";

import { LaborAmendPostEffectsLostError } from "@/encounters/components/EncounterLabor/utils/labor-amend-errors";
import { runLaborAmendPostAmendmentEffects } from "@/encounters/components/EncounterLabor/utils/labor-amend-post-amendment-effects";
import {
  clearAllLaborAmendSessionState,
  clearLaborAmendFlowsheetPartialCommitFromSession,
  clearLaborAmendPendingWorkFromSession,
  clearLaborAmendPostEffectsPendingFromSession,
  readLaborAmendFlowsheetPartialCommitFromSession,
  readLaborAmendPendingWorkFromSession,
  readLaborAmendPostEffectsPendingFromSession,
  writeLaborAmendFlowsheetPartialCommitToSession,
  writeLaborAmendPendingWorkToSession,
  writeLaborAmendPostEffectsPendingToSession,
} from "@/encounters/components/EncounterLabor/utils/labor-amend-session-state";
import type { LaborAmendStageMembershipChangeRecord } from "@/encounters/types/labor-amend-provenance";
import { setLaborAmendMembershipWriteGuard } from "@/encounters/utils/labor-amend-membership-write-guard";
import { toLaborAmendProvenanceTargetReference } from "@/encounters/utils/merge-labor-amend-provenance-targets";

import type {
  EnqueueFlowsheetPersistOptions,
  FlowsheetPersistQueueEntry,
  LaborAmendFlowsheetFlushResult,
} from "./types";

interface UseLaborAmendFlowsheetPersistQueueParams {
  encounterId: string;
  clearLocalPreviews: () => void;
  onDiscardAmendWork?: () => void;
}

/**
 * Owns the deferred flowsheet persist queue, flush/drain lifecycle, Sign & Lock locking, and
 * post-amendment effect orchestration for labor amend mode.
 */
export function useLaborAmendFlowsheetPersistQueue({
  encounterId,
  clearLocalPreviews,
  onDiscardAmendWork,
}: UseLaborAmendFlowsheetPersistQueueParams) {
  const queueRef = useRef<FlowsheetPersistQueueEntry[]>([]);
  const laborStageObservationsFlushCacheRef = useRef<Observation[] | undefined>(undefined);
  const isFlushInProgressRef = useRef(false);
  const flushInFlightRef = useRef<Promise<LaborAmendFlowsheetFlushResult> | null>(null);
  const signAndLockLockCountRef = useRef(0);
  const postAmendmentEffectsRef = useRef<Array<() => Promise<void>>>([]);
  const amendProvenanceTargetsRef = useRef<Reference<Resource>[]>([]);
  const amendProvenanceTargetReferencesRef = useRef<Set<string>>(new Set());
  const amendStageMembershipChangesRef = useRef<LaborAmendStageMembershipChangeRecord[]>([]);
  const [signAndLockLockCount, setSignAndLockLockCount] = useState(0);
  const [hasCommittedFlowsheetToServer, setHasCommittedFlowsheetToServer] = useState(() =>
    readLaborAmendFlowsheetPartialCommitFromSession(encounterId),
  );
  const [hasLostPendingWorkAfterRefresh, setHasLostPendingWorkAfterRefresh] = useState(() =>
    readLaborAmendPendingWorkFromSession(encounterId),
  );
  const [hasLostPostAmendmentEffectsAfterRefresh, setHasLostPostAmendmentEffectsAfterRefresh] =
    useState(() => readLaborAmendPostEffectsPendingFromSession(encounterId));
  const previousEncounterIdRef = useRef(encounterId);

  const markFlowsheetCommittedToServer = useCallback(() => {
    setHasCommittedFlowsheetToServer(true);
    writeLaborAmendFlowsheetPartialCommitToSession(encounterId);
  }, [encounterId]);

  const clearAmendProvenanceSnapshot = useCallback(() => {
    amendProvenanceTargetsRef.current = [];
    amendProvenanceTargetReferencesRef.current = new Set();
    amendStageMembershipChangesRef.current = [];
  }, []);

  const resetInMemoryStateForEncounter = useCallback(
    (targetEncounterId: string) => {
      queueRef.current = [];
      laborStageObservationsFlushCacheRef.current = undefined;
      isFlushInProgressRef.current = false;
      flushInFlightRef.current = null;
      postAmendmentEffectsRef.current = [];
      signAndLockLockCountRef.current = 0;
      setSignAndLockLockCount(0);
      setHasCommittedFlowsheetToServer(
        readLaborAmendFlowsheetPartialCommitFromSession(targetEncounterId),
      );
      setHasLostPendingWorkAfterRefresh(readLaborAmendPendingWorkFromSession(targetEncounterId));
      setHasLostPostAmendmentEffectsAfterRefresh(
        readLaborAmendPostEffectsPendingFromSession(targetEncounterId),
      );
      clearLocalPreviews();
      clearAmendProvenanceSnapshot();
    },
    [clearAmendProvenanceSnapshot, clearLocalPreviews],
  );

  useEffect(() => {
    if (previousEncounterIdRef.current === encounterId) {
      return;
    }
    previousEncounterIdRef.current = encounterId;
    resetInMemoryStateForEncounter(encounterId);
  }, [encounterId, resetInMemoryStateForEncounter]);

  const registerAmendProvenanceTarget = useCallback(
    (resourceOrReference: Resource | Reference<Resource>) => {
      const target = toLaborAmendProvenanceTargetReference(resourceOrReference);
      if (!target?.reference) {
        return;
      }
      if (amendProvenanceTargetReferencesRef.current.has(target.reference)) {
        return;
      }
      amendProvenanceTargetReferencesRef.current.add(target.reference);
      amendProvenanceTargetsRef.current.push(target);
    },
    [],
  );

  const registerAmendStageMembershipChange = useCallback(
    (change: LaborAmendStageMembershipChangeRecord) => {
      const existingIndex = amendStageMembershipChangesRef.current.findIndex(
        (entry) => entry.memberObservationId === change.memberObservationId,
      );
      if (existingIndex === -1) {
        amendStageMembershipChangesRef.current.push(change);
        return;
      }
      amendStageMembershipChangesRef.current[existingIndex] = change;
    },
    [],
  );

  const peekAmendProvenanceSnapshot = useCallback(
    () => ({
      targets: [...amendProvenanceTargetsRef.current],
      stageMembershipChanges: [...amendStageMembershipChangesRef.current],
    }),
    [],
  );

  const finalizeAmendProvenanceSnapshot = useCallback(() => {
    clearAmendProvenanceSnapshot();
  }, [clearAmendProvenanceSnapshot]);

  const canExecuteDeferredFlowsheetPersist = useCallback(() => isFlushInProgressRef.current, []);

  useEffect(() => {
    setLaborAmendMembershipWriteGuard({
      isDeferredAmendMode: () => true,
      canWriteMembership: () => isFlushInProgressRef.current,
    });
    return () => {
      setLaborAmendMembershipWriteGuard(undefined);
    };
  }, []);

  const commitAmendLocalPreviews = useCallback(() => {
    queueRef.current = [];
    setHasCommittedFlowsheetToServer(false);
    setHasLostPendingWorkAfterRefresh(false);
    clearLaborAmendPendingWorkFromSession(encounterId);
    clearLaborAmendFlowsheetPartialCommitFromSession(encounterId);
    clearLocalPreviews();
    clearAmendProvenanceSnapshot();
  }, [clearAmendProvenanceSnapshot, clearLocalPreviews, encounterId]);

  const setSignAndLockInProgress = useCallback((inProgress: boolean) => {
    if (inProgress) {
      signAndLockLockCountRef.current += 1;
    } else {
      signAndLockLockCountRef.current = Math.max(0, signAndLockLockCountRef.current - 1);
    }
    setSignAndLockLockCount(signAndLockLockCountRef.current);
  }, []);

  const registerPostAmendmentEffect = useCallback(
    (effect: () => Promise<void>) => {
      postAmendmentEffectsRef.current.push(effect);
      writeLaborAmendPostEffectsPendingToSession(encounterId);
      setHasLostPostAmendmentEffectsAfterRefresh(false);
    },
    [encounterId],
  );

  const runPostAmendmentEffects = useCallback(async () => {
    const effects = [...postAmendmentEffectsRef.current];
    if (effects.length > 0) {
      const { failedEffects, firstError } = await runLaborAmendPostAmendmentEffects(effects);
      postAmendmentEffectsRef.current = failedEffects;

      if (firstError) {
        throw firstError instanceof Error ? firstError : new Error(String(firstError));
      }
      return;
    }

    if (readLaborAmendPostEffectsPendingFromSession(encounterId)) {
      setHasLostPostAmendmentEffectsAfterRefresh(true);
      throw new LaborAmendPostEffectsLostError();
    }
  }, [encounterId]);

  const finalizePostAmendmentEffects = useCallback(() => {
    postAmendmentEffectsRef.current = [];
    setHasLostPostAmendmentEffectsAfterRefresh(false);
    clearLaborAmendPostEffectsPendingFromSession(encounterId);
  }, [encounterId]);

  const enqueueFlowsheetPersist = useCallback(
    (job: () => Promise<void>, options?: EnqueueFlowsheetPersistOptions): boolean => {
      if (signAndLockLockCountRef.current > 0) {
        return false;
      }
      const replaceKey = options?.replaceKey;
      if (replaceKey) {
        queueRef.current = queueRef.current.filter((entry) => entry.replaceKey !== replaceKey);
      }
      queueRef.current.push({ job, replaceKey });
      writeLaborAmendPendingWorkToSession(encounterId);
      setHasLostPendingWorkAfterRefresh(false);
      return true;
    },
    [encounterId],
  );

  const discardFlowsheetPersistQueue = useCallback(() => {
    queueRef.current = [];
    laborStageObservationsFlushCacheRef.current = undefined;
    isFlushInProgressRef.current = false;
    flushInFlightRef.current = null;
    postAmendmentEffectsRef.current = [];
    setHasCommittedFlowsheetToServer(false);
    setHasLostPendingWorkAfterRefresh(false);
    setHasLostPostAmendmentEffectsAfterRefresh(false);
    clearAllLaborAmendSessionState(encounterId);
    clearLocalPreviews();
    clearAmendProvenanceSnapshot();
    onDiscardAmendWork?.();
  }, [clearAmendProvenanceSnapshot, clearLocalPreviews, encounterId, onDiscardAmendWork]);

  const getLaborStageObservationsForFlush = useCallback(
    async (
      medplum: MedplumClient,
      clientId: string,
      targetEncounterId: string,
      fetchLaborStageObservations: (
        medplum: MedplumClient,
        clientId: string,
        encounterId: string,
      ) => Promise<Observation[]>,
    ): Promise<Observation[]> => {
      if (isFlushInProgressRef.current && laborStageObservationsFlushCacheRef.current) {
        return laborStageObservationsFlushCacheRef.current;
      }
      const observations = await fetchLaborStageObservations(medplum, clientId, targetEncounterId);
      if (isFlushInProgressRef.current) {
        laborStageObservationsFlushCacheRef.current = observations;
      }
      return observations;
    },
    [],
  );

  const runFlushBatch = useCallback(async (): Promise<LaborAmendFlowsheetFlushResult> => {
    isFlushInProgressRef.current = true;
    laborStageObservationsFlushCacheRef.current = undefined;

    let succeededJobCount = 0;
    while (queueRef.current.length > 0) {
      const entry = queueRef.current[0];
      try {
        await entry.job();
        queueRef.current.shift();
        succeededJobCount += 1;
        laborStageObservationsFlushCacheRef.current = undefined;
      } catch (error) {
        if (succeededJobCount > 0) {
          markFlowsheetCommittedToServer();
        }
        isFlushInProgressRef.current = false;
        laborStageObservationsFlushCacheRef.current = undefined;
        return {
          ok: false,
          error: error instanceof Error ? error : new Error(String(error)),
          succeededJobCount,
        };
      }
    }

    if (succeededJobCount > 0) {
      markFlowsheetCommittedToServer();
    }

    isFlushInProgressRef.current = false;
    laborStageObservationsFlushCacheRef.current = undefined;
    return { ok: true, succeededJobCount };
  }, [markFlowsheetCommittedToServer]);

  const flushFlowsheetPersistQueue =
    useCallback(async (): Promise<LaborAmendFlowsheetFlushResult> => {
      if (flushInFlightRef.current) {
        return flushInFlightRef.current;
      }

      const flushPromise = runFlushBatch();
      flushInFlightRef.current = flushPromise;

      try {
        return await flushPromise;
      } finally {
        if (flushInFlightRef.current === flushPromise) {
          flushInFlightRef.current = null;
        }
      }
    }, [runFlushBatch]);

  const drainFlowsheetPersistQueue =
    useCallback(async (): Promise<LaborAmendFlowsheetFlushResult> => {
      let succeededJobCount = 0;
      while (queueRef.current.length > 0 || flushInFlightRef.current) {
        const batchResult = flushInFlightRef.current
          ? await flushInFlightRef.current
          : await flushFlowsheetPersistQueue();
        succeededJobCount += batchResult.succeededJobCount;
        if (!batchResult.ok) {
          return {
            ok: false,
            error: batchResult.error,
            succeededJobCount,
          };
        }
      }
      return { ok: true, succeededJobCount };
    }, [flushFlowsheetPersistQueue]);

  const hasQueuedPersistJobs = useCallback(() => queueRef.current.length > 0, []);

  return useMemo(
    () => ({
      isFlowsheetPersistLocked: signAndLockLockCount > 0,
      hasCommittedFlowsheetToServer,
      hasLostPendingWorkAfterRefresh,
      setHasLostPendingWorkAfterRefresh,
      hasLostPostAmendmentEffectsAfterRefresh,
      enqueueFlowsheetPersist,
      flushFlowsheetPersistQueue,
      drainFlowsheetPersistQueue,
      commitAmendLocalPreviews,
      discardFlowsheetPersistQueue,
      setSignAndLockInProgress,
      registerPostAmendmentEffect,
      runPostAmendmentEffects,
      finalizePostAmendmentEffects,
      getLaborStageObservationsForFlush,
      hasQueuedPersistJobs,
      canExecuteDeferredFlowsheetPersist,
      registerAmendProvenanceTarget,
      registerAmendStageMembershipChange,
      peekAmendProvenanceSnapshot,
      finalizeAmendProvenanceSnapshot,
    }),
    [
      signAndLockLockCount,
      hasCommittedFlowsheetToServer,
      hasLostPendingWorkAfterRefresh,
      hasLostPostAmendmentEffectsAfterRefresh,
      enqueueFlowsheetPersist,
      flushFlowsheetPersistQueue,
      drainFlowsheetPersistQueue,
      commitAmendLocalPreviews,
      discardFlowsheetPersistQueue,
      setSignAndLockInProgress,
      registerPostAmendmentEffect,
      runPostAmendmentEffects,
      finalizePostAmendmentEffects,
      getLaborStageObservationsForFlush,
      hasQueuedPersistJobs,
      canExecuteDeferredFlowsheetPersist,
      registerAmendProvenanceTarget,
      registerAmendStageMembershipChange,
      peekAmendProvenanceSnapshot,
      finalizeAmendProvenanceSnapshot,
    ],
  );
}

export type LaborAmendFlowsheetPersistQueueState = ReturnType<
  typeof useLaborAmendFlowsheetPersistQueue
>;
